import {
  CATEGORY,
  experienceOptions,
  LOCATION_BASED,
  postingOptions,
  SEARCH,
  TAG,
} from '@jobseeker/common-core/constants';
import {AdsElementType} from '@jobseeker/common-core/types';
import dayjs from 'dayjs';
import {getWindow} from 'ssr-window';
import {PRIMARY_SOURCE_MAP} from '../constants/refCodes';
import {setCookie} from 'cookies-next';

const window = getWindow();

type ResponseType = {
  id?: string;
  Title: string;
  'Image Url'?: string;
  'Redirection Link': string;
  'Company Logo'?: string;
  'Allowed on JobFeeds'?: string;
  'Sub Text'?: string;
  'CTA Text'?: string;
  'Tracker Text'?: string;
  Text?: string;
  courseId: number;
  institute: string;
  minExp?: number;
  maxExp?: number;
  location?: any[];
  jobId?: number;
  ordering?: string;
  'Company Name'?: string;
  'Company Id'?: number;
  'Showcase Redirection Link'?: string;
  custId?: string;
  quoteLineNo?: string;
  centralisedCompanyId?: string;
};

function isObject(item: any): boolean {
  return item instanceof Object && !Array.isArray(item);
}

export function mergeDeep(...objects: any[]) {
  return objects.reduce((prev, obj) => {
    Object.keys(obj).forEach(key => {
      const pVal = prev[key];
      const oVal = obj[key];

      if (Array.isArray(pVal) && Array.isArray(oVal)) {
        prev[key] = pVal.concat(...oVal);
      } else if (isObject(pVal) && isObject(oVal)) {
        prev[key] = mergeDeep(pVal, oVal);
      } else {
        prev[key] = oVal;
      }
    });

    return prev;
  }, {});
}
type Tabs = {
  dataId: string;
  label: string;
  tag: string;
  menu?: Tabs[];
};

export const getInheritCatOrTagSlug = (
  tabList: Array<{dataId: string; label: string; tag: string; menu: Tabs[]}>,
  filteredLabel: string,
  cat?: boolean
) => {
  for (const tab of tabList) {
    if (tab.label === filteredLabel) {
      return {
        id: tab.dataId,
        slug: tab.tag,
        catId: parseInt(tab?.dataId),
      };
    }

    const filteredTab = tab.menu.filter(({label, menu}) => {
      if (label === filteredLabel) return true;

      if (Array.isArray(menu)) {
        return menu.some(({label}) => label === filteredLabel);
      }
      return false;
    });

    if (filteredTab.length > 0) {
      return {
        id: filteredTab[0]?.dataId,
        slug: filteredTab[0]?.tag,
        catId: filteredTab[0]?.dataId,
      };
    }
  }
};

const getAdResponse = (
  response: ResponseType[],
  categoryIds?: Array<number>
) => {
  const filteredResponse = response.filter(
    value => JSON.stringify(value) !== '{}'
  );

  const structuredAdResponse = filteredResponse.map(ad => {
    return {
      id: ad['id'],
      companyId: ad['Company Id'],
      title: ad.Title,
      companyLogo: ad['Company Logo'],
      imageUrl: ad['Image Url'],
      link: ad['Redirection Link'],
      description: ad['Text'],
      subText: ad['Sub Text'],
      ctaText: ad['CTA Text'],
      trackerText: ad['Tracker Text'],
      courseId: ad.courseId,
      institute: ad.institute,
      allowOnJobfeed: ad['Allowed on JobFeeds'],
      minExp: ad?.minExp,
      maxExp: ad?.maxExp,
      location: ad?.location,
      jobId: ad?.jobId,
      ordering: ad?.ordering,
      companyName: ad['Company Name'],
      showcaseRedirectionLink: ad['Showcase Redirection Link'],
      custId: ad?.custId,
      quoteLineNo: ad?.quoteLineNo,
      centralisedCompanyId: ad?.centralisedCompanyId,
      categoryIds,
    };
  });

  return structuredAdResponse;
};

const FALLBACK_URL = '/jobfeed';
const SELF_TARGET = '_self';

const getTrustedDomains = (): Set<string> => {
  return new Set(
    [
      window?.location?.hostname,
      process.env.NEXT_PUBLIC_DOMAIN,
      process.env.NEXT_PUBLIC_APP_DOMAIN,
    ]
      .map(domain => domain?.trim().toLowerCase().replace(/^\./, ''))
      .filter(Boolean) as string[]
  );
};

const isTrustedHostname = (hostname: string): boolean => {
  const normalizedHost = hostname.toLowerCase();
  return Array.from(getTrustedDomains()).some(
    domain => normalizedHost === domain || normalizedHost.endsWith(`.${domain}`)
  );
};

const getSafeUrl = (inputUrl: string, navTarget?: string): string | null => {
  const trimmedUrl = (inputUrl || '').trim();
  if (!trimmedUrl) return null;

  try {
    const parsedUrl = new URL(trimmedUrl, window.location.href);
    const protocol = parsedUrl.protocol.toLowerCase();

    if (
      ['javascript:', 'data:', 'vbscript:', 'file:', 'blob:'].includes(protocol)
    ) {
      return null;
    }

    if (!['http:', 'https:'].includes(protocol)) {
      return null;
    }

    if (navTarget === SELF_TARGET && !isTrustedHostname(parsedUrl.hostname)) {
      return null;
    }

    return parsedUrl.toString();
  } catch {
    return null;
  }
};

const deferTask = (callback: () => void): void => {
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => {
      setTimeout(callback, 0);
    });
  } else {
    setTimeout(callback, 0);
  }
};

const openUrl = (url: string, target?: string, features?: string): void => {
  const safeUrl = getSafeUrl(url, target);

  if (!safeUrl) {
    if (target === SELF_TARGET) {
      window.open(FALLBACK_URL, SELF_TARGET, features);
    }
    return;
  }

  const prefText = getPrefText();
  let finalUrl = safeUrl;

  // Append prefText as a query parameter if it has a value
  if (prefText) {
    const separator = finalUrl.includes('?') ? '&' : '?';
    finalUrl += `${separator}pref=${encodeURIComponent(prefText)}`;
  }

  const newWindow = window.open(finalUrl, target, features);
  if (newWindow) newWindow.opener = null;
};

const getRandomElementFromArray = (
  list: Array<AdsElementType | object>,
  noOfRandom: number
) => {
  if (list?.length) {
    const shuffled = [...list].sort(() => 0.5 - Math.random());

    return shuffled?.slice(0, noOfRandom);
  }
};

export const flatifyObject = (obj: {[key: string]: string | string[]}) => {
  const newObj: any = {};
  for (const k in obj) {
    if (Array.isArray(obj[k])) {
      newObj[k] = (obj[k] as string[]).join(',');
    } else {
      newObj[k] = obj[k];
    }
  }
  return newObj;
};

const getExperienceIndex = (exp: number): number => {
  if (exp >= 0 && exp <= 3) return 1;
  if (exp >= 4 && exp <= 6) return 2;
  if (exp >= 7 && exp <= 10) return 3;
  if (exp >= 11 && exp <= 15) return 4;
  if (exp >= 15) return 5;
  return 0;
};

export type ExpRange = {
  minexp: number | string;
  maxexp: number | string;
};
const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

const EXPERIENCE_MAPPING: Map<number, ExpRange> = new Map([
  [1, {minexp: 0, maxexp: 30}],
  [2, {minexp: 0, maxexp: 1}],
  [3, {minexp: 2, maxexp: 3}],
  [4, {minexp: 4, maxexp: 6}],
  [5, {minexp: 7, maxexp: 10}],
  [6, {minexp: 11, maxexp: 15}],
  [7, {minexp: 16, maxexp: 20}],
  [8, {minexp: 21, maxexp: 25}],
  [9, {minexp: isAppIimjobs ? 25 : 26, maxexp: 100}],
]);

// const EXPERIENCE_MAPPING: Map<number, ExpRange> = new Map([
//   [1, {minexp: 0, maxexp: 30}],
//   [2, {minexp: 1, maxexp: 3}],
//   [3, {minexp: 4, maxexp: 6}],
//   [4, {minexp: 7, maxexp: 10}],
//   [5, {minexp: 11, maxexp: 15}],
//   [6, {minexp: 15, maxexp: 100}],
// ]);

// const EXPERIENCE_MAPPING = [
//   {minexp: 0, maxexp: 30},
//   {minexp: 1, maxexp: 3},
//   {minexp: 4, maxexp: 6},
//   {minexp: 7, maxexp: 10},
//   {minexp: 11, maxexp: 15},
//   {minexp: 15, maxexp: 100},
// ];

const getExperienceRange = (expIndex: number): ExpRange => {
  const range = EXPERIENCE_MAPPING.get(expIndex);
  if (!range) {
    return {maxexp: 0, minexp: 0};
  }
  return range;
};

const getExperienceIndexByRange = (range: ExpRange) => {
  const values = Array.from(EXPERIENCE_MAPPING.values());
  const index = values.findIndex(
    expRange =>
      1 || (expRange.minexp == range.minexp && expRange.maxexp == range.maxexp)
  );

  return index >= 0 ? index + 1 : null;
};
const getQueryParams = () => {
  if (typeof window === 'undefined') {
    return {};
  }

  return Object.fromEntries(new URLSearchParams(window.location.search));
};

const getQueryParamsAndSeekerId = (existingUrl?: string) => {
  const url = existingUrl ?? window.location.href;
  const urlObj = new URL(url);
  const queryParams = Object.fromEntries(urlObj.searchParams.entries());

  return {queryParams};
};

const getRefTagAndOrigin = () => {
  if (typeof window === 'undefined') {
    return {
      origin: '',
      refText: '',
    };
  }
  const url = window.location.href || '';
  const queryParams = getQueryParams();
  const refText = queryParams['ref'] || '';
  let origin = '';
  if (url.includes('/c/')) {
    origin = 'CategoryPage';
  } else if (url.includes('/k/')) {
    origin = 'KeywordPage';
  } else if (url.includes('/search/')) {
    origin = 'Search';
  } else if (url.includes('/similar_jobs.php')) {
    origin = 'SimilarJob';
  } else if (url.includes('/saved-jobs')) {
    origin = 'SavedJob';
  } else if (url.includes('/applied-jobs')) {
    origin = 'AppliedJob';
  } else if (url.includes('/allnotification')) {
    origin = 'NotificationPage';
  } else if (url.includes('/login')) {
    origin = 'home';
  } else {
    origin = 'Jobfeed';
  }
  return {origin, refText};
};

const getDeviceOs = (userAgent: string): string => {
  const isMobile =
    /(iPad|iPhone|Android|webOS|iPod|BlackBerry|IEMobile|Opera Mini|Mobile)/i.test(
      userAgent
    ) || false;

  if (isMobile) {
    if (/android/i.test(userAgent)) {
      return 'android';
    }

    if (/iphone|ipad|ipod|iPhone|ios/.test(userAgent)) {
      return 'ios';
    }
  }

  return 'web';
};

const getAllParamsFromUrl = () => {
  if (window && window.location) {
    // Get params from regular query string
    const queryParams = new URLSearchParams(window.location?.search);
    const params = Object.fromEntries(queryParams.entries());

    // Also extract params from hash fragment (for URLs like #signup?utm_source=google)
    const hash = window.location?.hash || '';
    const hashParamsIndex = hash.indexOf('?');
    if (hashParamsIndex > -1) {
      const hashParams = new URLSearchParams(
        hash.substring(hashParamsIndex + 1)
      );
      hashParams.forEach((value, key) => {
        // Hash params take precedence for hash-based URLs
        if (!params[key]) {
          params[key] = value;
        }
      });
    }

    return params;
  }
};

const convertDate = (
  dateStr: string,
  inputFormat: string,
  outputFormat: string
): string => {
  const date = dayjs(dateStr, inputFormat);
  if (!date.isValid()) {
    return 'Invalid Date';
  }
  return date.format(outputFormat);
};

const appendQueryParams = (
  url: string,
  params: {[key: string]: string}
): string => {
  const queryString = Object.entries(params)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .join('&');

  const separator = url.includes('?') ? '&' : '?';
  return queryString.length ? url + separator + queryString : url;
};

export const computeSearchUrlNew = (
  searchString = '',
  location = '',
  exp = 'Any Exp. Level',
  catId = '',
  pageNo = 0,
  range = '',
  page = 'search'
) => {
  let pageIndexFlag = pageNo ? `-p${pageNo + 1}` : '';
  let computedUrl = `/search/${searchString}${pageIndexFlag}.html?locIds=${
    page === 'course' ? location : location.length ? location.concat('_') : 0
  }&exp=${experienceOptions.find(e => e.name === exp)?.id}`;
  if (range && range !== 'Posting') {
    computedUrl += `&range=${postingOptions.find(e => e.name === range)?.id}`;
  }
  if (page === 'course') {
    computedUrl += `&search=Courses&cat=${catId}`;
  }
  if (page === 'company') {
    computedUrl += `&search=Companies&cat=${catId}`;
  }
  return computedUrl;
};
function replaceAll(string: any, stringToReplace: any, replaceWith: any) {
  return string.split(stringToReplace).join(replaceWith);
}

function sanitizeTag(tag: any) {
  tag = replaceAll(tag, '+', '-plus');
  tag = replaceAll(tag, 'c#', 'c-sharp');
  tag = replaceAll(tag, '#', 'hash');
  tag = replaceAll(tag, '.', '');
  return tag;
}

const createPopularTagUrl = (tag: any, type: any) => {
  let category = tag.id
    ? tag.key.toLowerCase()
    : tag.searchString.toLowerCase();
  if (tag.id === 0) {
    let searchURL = computeSearchUrlNew(tag.searchString);
    return searchURL;
  }
  category = category.replace(/-/g, '');
  category = category.replace(/&/g, '');
  category = category.replace(/\s+/g, '-');
  category = category.replace(/\//g, '-');
  category = category.replace(/-jobs/g, '');
  category = sanitizeTag(category);
  category = category.replace(/#/g, '-hash');
  const url = `/${type}/${category}-jobs`;
  return url;
};

const getKeyFromPath = (key: string) => {
  if (window && window.location) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(key);
  }
  return '';
};

const replacePlaceholders = (
  template: string,
  data: {[key: string]: string}
) => {
  const dataInfo = template.replace(/{{(.*?)}}/g, (_, key) => data[key] ?? '');
  return dataInfo;
};

const parseShowcaseUrl = (url: string) => {
  let array = [];
  if (url) {
    array = url.split('-');
    const companyId = array[array.length - 1].split('.')[0];
    return !isNaN(parseInt(companyId ?? '')) ? companyId : 0;
  }
  return 0;
};

const getCompanyNameFromShowcase = (url: string): string => {
  try {
    const filename = new URL(url).pathname.split('/').pop() || '';
    const parts = filename.replace('.html', '').split('-');

    return parts.length >= 3 ? parts.slice(1, -1).join(' ') : '';
  } catch {
    return '';
  }
};

const getPrefText = () => {
  const queryParams = getQueryParams();
  let prefText = queryParams['pref'];
  const refText = queryParams['ref'];
  const utm_source = queryParams['utm_source'];
  if (!prefText && Object.keys(PRIMARY_SOURCE_MAP).includes(refText)) {
    prefText = refText;
  }
  prefText = utm_source ? utm_source : prefText;
  return prefText;
};

const getAppDownloadLinkagesUrls = (type: 1 | 2 | 3 | 4 | 5) => {
  const urls: Record<string, string> = {
    1: 'https://hirist.onelink.me/Z1Mi/1tqdw24l', // Login Page Download App Button
    2: 'https://hirist.onelink.me/Z1Mi/r14pzu5o', // Hamburger Menu Download App Button
    3: 'https://hirist.onelink.me/Z1Mi/z6nebyoy', // Floating Download (On listing pages)
    4: 'https://hirist.onelink.me/Z1Mi/cepggp92', // Download Bottomsheet (After Apply)
    5: 'https://hirist.onelink.me/Z1Mi/jj7noi6w', // Use App Button (Top Menu)
  };
  console.log('app redirect url', urls[String(type)]);
  return urls[String(type)] ?? undefined;
};

const getItemWithExpiry = (key: string): any | null => {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return null;
  }
  const itemStr = localStorage.getItem(key);
  if (!itemStr) return null;
  const {value, expiry} = JSON.parse(itemStr);
  if (Date.now() > expiry) {
    localStorage.removeItem(key);
    return null;
  }
  return value;
};

const formQueryParams = (ref: string) => {
  const params = ['utm_source', 'utm_medium', 'utm_campaign'];
  const finalRef = ref || getKeyFromPath('ref');
  let query = '';

  if (finalRef && finalRef !== 'null' && finalRef !== 'undefined') {
    query += `ref=${finalRef}`;
  }

  params.forEach(key => {
    const val = getKeyFromPath(key);
    if (val) query += query ? `&${key}=${val}` : `${key}=${val}`;
  });
  return query;
};

const getExpFilter = (feedType: string) => {
  if (
    (feedType === CATEGORY ||
      feedType === TAG ||
      feedType === LOCATION_BASED) &&
    process.env.NEXT_PUBLIC_FILTER_CAT_TAG === 'true'
  ) {
    return JSON.parse(localStorage.getItem('filterCat') || '{}');
  }
  return JSON.parse(localStorage.getItem('filter') || '{}');
};

const setExpFilter = (feedType: string, value: any) => {
  if (process.env.NEXT_PUBLIC_FILTER_CAT_TAG === 'true') {
    if (
      feedType === CATEGORY ||
      feedType === TAG ||
      feedType === LOCATION_BASED
    ) {
      localStorage.setItem('filterCat', JSON.stringify(value));
      setCookie('filterCat', value);
      return;
    }
    if (feedType === SEARCH) {
      localStorage.setItem('filter', JSON.stringify(value));
      setCookie('filter', value);
      localStorage.setItem('filterCat', JSON.stringify(value));
      setCookie('filterCat', value);
      return;
    }
  }
  localStorage.setItem('filter', JSON.stringify(value));
  setCookie('filter', value);
  return;
};
const truncateHtmlAtWords = (html: string, limit: number): string => {
  let wordCount = 0;
  let inTag = false;
  let prevWasSpace = true;
  for (let i = 0; i < html.length; i++) {
    if (html[i] === '<') inTag = true;
    else if (html[i] === '>') {
      inTag = false;
      prevWasSpace = true;
    } else if (!inTag) {
      const isSpace = /\s/.test(html[i]);
      if (!isSpace && prevWasSpace) {
        wordCount++;
        if (wordCount > limit) return html.substr(0, i).trim();
      }
      prevWasSpace = isSpace;
    }
  }
  return html;
};

const sanitizeSearchQuery = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 200);

const HTML_ENTITY_MAP: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  ndash: '–',
  mdash: '—',
  lsquo: '‘',
  rsquo: '’',
  ldquo: '“',
  rdquo: '”',
  hellip: '…',
  bull: '•',
  middot: '·',
  copy: '©',
  reg: '®',
  trade: '™',
  deg: '°',
  eacute: 'é',
};

const decodeHtmlEntities = (value?: string | null): string => {
  if (!value || typeof value !== 'string' || value.indexOf('&') === -1)
    return value ?? '';

  return value.replace(
    /&(#x?[0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]{1,31});/g,
    (match, entity: string) => {
      if (entity[0] === '#') {
        const codePoint =
          entity[1] === 'x' || entity[1] === 'X'
            ? parseInt(entity.slice(2), 16)
            : parseInt(entity.slice(1), 10);
        if (
          !Number.isFinite(codePoint) ||
          codePoint <= 0 ||
          codePoint > 0x10ffff ||
          (codePoint >= 0xd800 && codePoint <= 0xdfff)
        )
          return match;
        try {
          return String.fromCodePoint(codePoint);
        } catch {
          return match;
        }
      }
      const named = HTML_ENTITY_MAP[entity.toLowerCase()];
      return named ?? match;
    }
  );
};

export {
  appendQueryParams,
  convertDate,
  createPopularTagUrl,
  FALLBACK_URL,
  deferTask,
  getAdResponse,
  getAllParamsFromUrl,
  getDeviceOs,
  getExperienceIndex,
  getExperienceIndexByRange,
  getExperienceRange,
  getKeyFromPath,
  getPrefText,
  getCompanyNameFromShowcase,
  getQueryParams,
  getQueryParamsAndSeekerId,
  getRandomElementFromArray,
  getRefTagAndOrigin,
  getSafeUrl,
  getTrustedDomains,
  isTrustedHostname,
  openUrl,
  parseShowcaseUrl,
  replacePlaceholders,
  sanitizeTag,
  SELF_TARGET,
  getAppDownloadLinkagesUrls,
  getItemWithExpiry,
  formQueryParams,
  getExpFilter,
  setExpFilter,
  truncateHtmlAtWords,
  sanitizeSearchQuery,
  decodeHtmlEntities,
};
